// <div data-controller="fonts--search">
//
// </div>
import { Controller } from "@hotwired/stimulus";;
import api from "@/utils/api";
import {LoadingSpinner, SearchIcon} from "@/utils/helpers";

export default class extends Controller {
  static targets = [
    "container",
    "searchInput",
    "results",
    "selectedId",
    "selectedName",
    "submitButton",
    "cancelButton"
  ];

  static classes = [""]

  static values = {
    filterApi: String,
    page: Number,
    params: Object,
    loading: Boolean,
    finished: Boolean,
    cdnHost: String,
    searchingKeyword: String,
    enterSubmit: Boolean,
    openResult: Boolean,
    defaultText: String,
    notFoundText: String,
    searchBasePath: String
  };

  initialize() {
    this.searchableTimeout = null;
    this.activeIndex = -1;
  }

  focusSearchInput = (e) => {
    this.searchInputTarget.focus();
  }

  onTypeSearchInput = (e) => {
    if (this.searchableTimeout != null) {
      clearTimeout(this.searchableTimeout);
    }

    const trimKeyword = e.target.value.trim();
    if (trimKeyword === "") {
      this.clearSearchSelection();
      return;
    }

    // 검색어가 그대로면(방향키 등으로 keyup 만 발생한 경우) 하이라이트를 유지한다.
    // 여기서 초기화하면 키보드 이동 직후 keyup 으로 활성 항목이 풀려 이동이 막힌다.
    if (trimKeyword === this.searchingKeywordValue) {
      return;
    }

    // 새 검색어로 바뀐 경우에만 하이라이트를 초기화한다.
    this.resetActiveItem();
    this.startLoading();
    this.searchableTimeout = setTimeout(() => {
      this.searchableTimeout = null;
      this.startSearching(e.target.value)
    }, 500)
  }

  onCancelInput = (e) => {
    this.searchInputTarget.value = null;
  }

  showResults = (e) => {
    this.resultsTarget.classList.remove("hidden")
    this.positionResults()
  }

  positionResults = () => {
    if (window.innerWidth >= 768) return;
    const formRect = this.searchInputTarget.closest("form").getBoundingClientRect();
    const margin = 8;
    this.resultsTarget.style.left = `${-formRect.left + margin}px`;
    this.resultsTarget.style.width = `${window.innerWidth - margin * 2}px`;
  }

  hideResults = (e) => {
    this.resultsTarget.classList.add("hidden")
  }

  // ===== 키보드 위/아래 탐색 =====
  // 선택 가능한 항목만(클릭 액션이 있는 li 또는 링크) 대상으로 한다. 안내/빈 결과 li 는 제외.
  resetActiveItem = () => {
    this.activeIndex = -1;
  }

  navigableItems = () => {
    return Array.from(this.resultsTarget.querySelectorAll("li[data-action], a[href]"));
  }

  highlightActiveItem = (items) => {
    items.forEach((el, index) => {
      const active = index === this.activeIndex;
      el.classList.toggle("bg-gray-100", active);
      el.classList.toggle("dark:bg-gray-800", active);
      if (active) el.scrollIntoView({ block: "nearest" });
    });
  }

  moveActiveItem = (delta) => {
    const items = this.navigableItems();
    if (items.length === 0) return;
    let next = this.activeIndex + delta;
    if (next < 0) next = items.length - 1;
    else if (next >= items.length) next = 0;
    this.activeIndex = next;
    this.highlightActiveItem(items);
  }

  onKeydownSearchInput = (e) => {
    const isHidden = this.resultsTarget.classList.contains("hidden");
    switch (e.key) {
      case "ArrowDown":
        if (isHidden) return;
        e.preventDefault();
        this.moveActiveItem(1);
        break;
      case "ArrowUp":
        if (isHidden) return;
        e.preventDefault();
        this.moveActiveItem(-1);
        break;
      case "Enter": {
        const active = this.navigableItems()[this.activeIndex];
        if (active) {
          // 하이라이트된 항목에서 엔터 = 클릭과 동일.
          e.preventDefault();
          this.resetActiveItem();
          active.click();
        }
        break;
      }
      case "Escape":
        this.resetActiveItem();
        this.hideResults();
        break;
    }
  }

  startLoading = () => {
    this.loadingValue = true;
    this.submitButtonTarget.innerHTML = LoadingSpinner;
  }

  afterLoading = () => {
    this.loadingValue = false;
    this.submitButtonTarget.innerHTML = SearchIcon;
  }

  startSearching = (keyword = "") => {
    const trimKeyword = keyword.trim();
    const prevKeyword = this.searchingKeywordValue;
    if (prevKeyword !== trimKeyword) {
      this.pageValue = 1;
    }
    const currentPage = this.hasPageValue ? this.pageValue : 1;
    this.searchingKeywordValue = trimKeyword;
    let query = {page: currentPage}; // , ...this.paramsValue
    if (trimKeyword) {
      query["search"] = trimKeyword;
      this.startLoading();
      api.get(`${this.filterApiValue}.json`, query)
        .then((res) => {
          const {fonts, is_last_page, total_count} = res.data;
          if (total_count === 0) {
            this.renderEmpty();
          } else {
            if (prevKeyword !== trimKeyword) {
              this.resultsTarget.innerHTML = null;
            }
            this.renderResults(fonts);
            this.pageValue = currentPage + 1;
          }
          this.afterLoading();
          this.finishedValue = is_last_page;
        })
    } else {
      this.renderDefault();
      this.afterLoading();
    }
  }

  onScroll() {
    if ((this.resultsTarget.scrollTop + this.resultsTarget.clientHeight) >= this.resultsTarget.scrollHeight - 70) {
      if (!this.loadingValue && !this.finishedValue) {
        this.startSearching(this.searchingKeywordValue);
      }
    }
  }

  clearSearchSelection = () => {
    this.pageValue = 1;
    this.loadingValue = false;
    this.searchingKeywordValue = null
    this.afterLoading();
    this.renderDefault();
    if (this.hasSelectedIdTarget) {
      this.selectedIdTarget.value = null;
    }
    if (this.hasSelectedNameTarget) {
      this.selectedNameTarget.value = null;
    }
  }

  renderDefault = () => {
    this.resetActiveItem();
    this.resultsTarget.innerHTML = null;
    const empty = `<li class="relative py-2 px-3 min-h-12 flex items-center justify-center text-gray-500 dark:text-gray-300">${this.defaultTextValue}</li>`;
    this.resultsTarget.insertAdjacentHTML('beforeend', empty)
  }

  renderEmpty = () => {
    this.resetActiveItem();
    this.resultsTarget.innerHTML = null;
    const empty = `<li class="relative py-2 px-3 min-h-12 flex items-center justify-center text-gray-500 dark:text-gray-300">${this.notFoundTextValue}</li>`;
    this.resultsTarget.insertAdjacentHTML('beforeend', empty)
  }

  renderResults = (fonts, controller = "fonts--search") => {
    let templates = [];
    fonts.forEach((font) => {
      templates.push(this.fontTemplate(font, controller))
    });
    this.resultsTarget.insertAdjacentHTML('beforeend', templates.join("\n"))
  }

  fontTemplate(font, controller = "fonts--search") {
    const {
      id, name, style, ph_content,
      loaded_current_user_font,
      creator_description, thumbnail_cdn_key, font_family_token, cdn_server_html,
      polymorphic_show_path, is_market
    } = font;

    let fontImage;

    if (thumbnail_cdn_key) {
      fontImage = `<img class="h-6 w-auto object-cover font-image-preview"
                         src="${this.cdnHostValue}/${thumbnail_cdn_key}"
                         alt="${name}">`;
    } else {
      fontImage = `<style>${cdn_server_html}</style><span style="${style};" id="fontEditor${id}">${name}</span>`;
    }

    const badgeHtml = is_market
      ? `<span class="ml-2 shrink-0 text-xs px-1.5 py-0.5 border border-zinc-200 dark:border-zinc-600 rounded">마켓</span>`
      : '';

    const fontDetail = `<div class="flex items-center pointer-events-none min-w-0 overflow-hidden"><span class="shrink-0 text-black dark:text-white">${fontImage}</span>${badgeHtml}<span class="font-normal text-gray-600 dark:text-gray-400 ml-3 truncate">${creator_description}</span></div>`;
    // 폰트를 열어 줌
    if (this.openResultValue === true) {
      return `<a href="${polymorphic_show_path}" class="group block select-none relative py-2 px-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800">${fontDetail}</a>`;
    } else {
      return `<li class="group select-none relative py-2 px-3 text-gray-900 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                data-font-id="${id}" data-font-name="${name}"
                data-action="click->${controller}#onClickResult">${fontDetail}</li>`;
    }
  }

  onClickResult = (e) => {
    const {fontId, fontName} = e.target.dataset;
    if (this.hasSelectedNameTarget) {
      this.selectedNameTarget.value = fontName;
    }
    if (this.hasSelectedIdTarget) {
      this.selectedIdTarget.value = fontId;
    }
    this.hideResults();
  }

  submitToSearchPath = (e) => {
    e.preventDefault();
    const keyword = this.searchInputTarget.value.trim();
    if (!keyword) return;
    window.location.href = `${this.searchBasePathValue}/${encodeURIComponent(keyword)}`;
  }

  preventEnterSubmit = (e) => {
    const code = e.keyCode || e.which;
    if (code === 13) {
      e.preventDefault();
      return false;
    }
  }

  containerClosable = (e) => {
    if (!this.containerTarget.contains(e.target)) {
      this.resultsTarget.classList.add("hidden")
    }
  }

  connect() {
    if (this.enterSubmitValue === false) {
      this.searchInputTarget.addEventListener("keypress", this.preventEnterSubmit)
    }
    this.searchInputTarget.addEventListener("keydown", this.onKeydownSearchInput)
    document.addEventListener("click", this.containerClosable)
  }

  disconnect() {
    if (this.searchableTimeout) {
      clearTimeout(this.searchableTimeout);
    }
    if (this.enterSubmitValue === false) {
      this.searchInputTarget.removeEventListener("keypress", this.preventEnterSubmit)
    }
    this.searchInputTarget.removeEventListener("keydown", this.onKeydownSearchInput)
    document.removeEventListener("click", this.containerClosable)
  }
}